import ReactSelect from '@/components/UI/Select';
import { _isEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';
import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import '../MetadataFields.scss';

export interface SelectOption {
  label: string;
  value: string;
}

export const renderLabel = (value: string | Array<string>): string => {
  if (_notNil(value)) {
    if (Array.isArray(value)) {
      return value.map((v) => safelyDecodeURIComponent(v)).join(', ');
    }
    return safelyDecodeURIComponent(value);
  }
  return '';
};

export const renderLookupLabel = (value: string | Array<string>): string => {
  if (_notNil(value)) {
    return Array.isArray(value) ? value.join(', ') : value;
  }
  return '';
};

const defaultValueWithLabel = (
  field: MetadataFieldWithValue,
  isMulti: boolean,
  toLabel: (value: string) => string
): SelectOption | Array<SelectOption> | undefined => {
  if (_notNil(field.value)) {
    if (isMulti) {
      if (!Array.isArray(field.value)) {
        return [];
      }
      return (field.value as Array<string>).map((f) => ({ label: toLabel(f), value: f }));
    }
    return { label: toLabel(field.value as string), value: field.value as string };
  }
};

export const lookupDefaultValue = (
  field: MetadataFieldWithValue,
  isMulti: boolean
): SelectOption | Array<SelectOption> | undefined => defaultValueWithLabel(field, isMulti, renderLookupLabel);

export const defaultValue = (
  field: MetadataFieldWithValue,
  isMulti: boolean
): SelectOption | Array<SelectOption> | undefined => defaultValueWithLabel(field, isMulti, renderLabel);

export const handleChange = (
  onChange: (value: string | Array<string>) => void,
  data: SelectOption | Array<SelectOption> | null,
  isMulti: boolean
): void => {
  if (_notNil(data)) {
    if (isMulti) {
      onChange((data as Array<SelectOption>).map((d) => d.value));
    } else {
      onChange((data as SelectOption).value);
    }
  } else {
    onChange('');
  }
};

interface SelectProps {
  field: MetadataFieldWithValue;
  isMulti?: boolean;
  testId?: string;
}

export const Select: FC<SelectProps> = ({ field, isMulti = false, testId = 'select' }) => {
  const { control } = useFormContext();

  return (
    <div className="metadata-select-container" data-testid={testId}>
      <Controller
        defaultValue={field.value}
        name={`metadata[${field.glossary_api_id}].value`}
        rules={{
          validate: {
            required: (value: string | Array<string>) => {
              if (field.required === true && _isEmpty(value)) {
                return 'This field is required';
              }
              return true;
            },
          },
        }}
        control={control}
        render={({ field: { onChange } }) => (
          // @ts-expect-error - ReactSelect uses discriminated union for isMulti which doesn't work with dynamic prop
          <ReactSelect
            defaultValue={defaultValue(field, isMulti)}
            options={(field.options ?? []).map((value: string) => ({
              value: value,
              label: renderLabel(value),
            }))}
            isMulti={isMulti}
            onChange={(data: SelectOption | Array<SelectOption> | null) => handleChange(onChange, data, isMulti)}
          />
        )}
      />
    </div>
  );
};
